<template>
  <section>
    <form method="post" @submit.prevent="onSend" novalidate>
      <div class="modal-card" style="width: 860px">
        <header class="modal-card-head">
          <p class="modal-card-title is-centered">
            {{ formTitle }}
          </p>
        </header>
        <section class="modal-card-body">
          <form-wrapper :validator="$v.form">
            <form-group
              horizontal
              name="group_code"
              :label="$t('attributes.group_code')"
            >
              <b-autocomplete
                ref="product_code_input"
                v-model="form.group_code"
                name="group_code"
                field="code"
                :data="autoCompleteTravelGroups"
                :loading="loadingAutoCompleteTravelGroups"
                :placeholder="$t('message.please_input_group_code')"
                :open-on-focus="false"
                :disabled="!isCreate"
                @typing="loadAutoCompleteTravelGroups"
                @select="onSelectedTravelGroup"
              >
                <template #default="props">
                  <p>
                    {{ props.option.code }} -
                    {{ props.option.departure_date.split('T')[0] }} -
                    <!-- eslint-disable-next-line-->
                    <span v-html="props.option.name"></span>
                  </p>
                </template>
                <template #empty>
                  {{ form.group_code }}
                  {{
                    form.group_code
                      ? isLoadingProducts
                        ? $t('attributes.searching')
                        : $t('attributes.not_found')
                      : $t('message.please_input_product_code')
                  }}
                </template>
              </b-autocomplete>
            </form-group>
            <form-group
              horizontal
              name="group_departure_date"
              :label="$t('attributes.departure_date')"
            >
              {{ form.group_departure_date.split('T')[0] }}
            </form-group>
            <form-group horizontal name="sort" :label="$t('attributes.sort')">
              <b-select v-model="form.sort" name="sort">
                <option
                  v-for="option in sortOptions"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </b-select>
            </form-group>
            <form-group
              horizontal
              name="status"
              attribute="attributes.status"
              :label="$t('attributes.want_display')"
            >
              <b-switch v-model="form.status" name="status"></b-switch>
            </form-group>
            <form-group
              horizontal
              name="picture"
              attribute="attributes.picture"
              :label="$t('attributes.picture')"
              grouped
            >
              <template #label>
                {{ $t('attributes.picture') }}
                <b-tooltip type="is-dark" label="1920*640/限定jpg檔，600KB內">
                  <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                </b-tooltip>
              </template>
              <b-field class="file">
                <a class="button is-primary" @click="onSelectPictureImage">
                  <b-icon icon="upload"></b-icon>
                  <span>{{ $t('message.please_select_file') }}</span>
                </a>
                <p
                  v-if="form.picture"
                  class="tag is-warning"
                  style="margin-left: 4px; height: 40px"
                >
                  <a
                    :href="projectImageUrl(form.picture)"
                    @click.prevent="onClickImage(projectImageUrl(form.picture))"
                  >
                    {{ form.picture }}
                  </a>
                </p>
              </b-field>
            </form-group>
            <form-group horizontal name="name" :label="$t('attributes.name')">
              <b-input
                v-model="form.name"
                name="name"
                :placeholder="
                  $t('message.please_input') + $t('attributes.name')
                "
                expanded
              >
              </b-input>
            </form-group>
            <form-group
              horizontal
              name="remark"
              :label="$t('attributes.bottom') + $t('attributes.description')"
            >
              <b-input
                v-model="form.remark"
                type="textarea"
                name="remark"
                :placeholder="
                  $t('message.please_input') +
                    $t('attributes.bottom') +
                    $t('attributes.description')
                "
                expanded
              ></b-input>
            </form-group>
          </form-wrapper>
        </section>
        <footer class="modal-card-foot">
          <b-button class="is-fullwidth" @click="$parent.close()">
            {{ $t('attributes.close') }}
          </b-button>
          <b-button
            :loading="sending"
            type="is-primary"
            class="is-fullwidth"
            native-type="submit"
          >
            {{ $t('attributes.update') }}
          </b-button>
        </footer>
      </div>
    </form>
    <b-modal :active.sync="isImageModalActive">
      <p class="image">
        <img :src="selectedImageUrl" alt="" />
      </p>
    </b-modal>
  </section>
</template>

<script>
import { maxLength, required } from 'vuelidate/lib/validators'
import { OpenProjectFinder } from '@/utils/ckfinder'
import notifyService from '@/services/notifyService'
import { HONEY_MOON_API } from '@/utils/apis'
import debounce from 'lodash/debounce'

export default {
  name: 'HoneyMoonTravelGroupForm',
  props: {
    travelGroup: {
      type: Object,
      required: true
    },
    isCreate: {
      type: Boolean
    }
  },
  data() {
    return {
      sending: false,

      form: this.extractFormValues(this.travelGroup),

      sortOptions: [...Array(20).keys()].map(i => i + 1),

      isImageModalActive: false,
      selectedImageUrl: null,

      autoCompleteTravelGroups: [],
      selectedAutoCompleteTravelGroup: null,
      loadingAutoCompleteTravelGroups: false
    }
  },
  validations: {
    form: {
      group_code: {},
      id: {},
      sort: { required },
      status: { required },
      group_departure_date: {},
      name: { required, maxLength: maxLength(500) },
      picture: { required },
      remark: { required },
      area_code: {},
      product_code: {},
      catalog_no: {}
    }
  },
  computed: {
    formTitle() {
      let title = !this.isCreate
        ? this.$t('attributes.modify')
        : this.$t('attributes.add')
      title += this.$t('attributes.honey_moon_page')
      title += this.$t('attributes.travel_group')
      return title
    }
  },
  watch: {
    travelGroup(newTravelGroup) {
      this.form = this.extractFormValues(newTravelGroup)
    }
  },
  methods: {
    extractFormValues(data) {
      return {
        id: data.id,
        group_code: data.group_code,
        group_departure_date: data.group_departure_date,
        sort: data.sort,
        status: data.status,
        name: data.name,
        picture: data.picture,
        remark: data.remark,
        area_code: data.area_code,
        product_code: data.product_code,
        catalog_no: data.catalog_no
      }
    },
    async loadAutoCompleteTravelGroupsAsync() {
      const api = `${HONEY_MOON_API}/autocomplete/travel_group`

      const params = {
        code: this.form.group_code
      }
      this.loadingAutoCompleteTravelGroups = true
      try {
        const res = await this.axios.get(api, { params })
        this.autoCompleteTravelGroups = res.data.data
      } catch (e) {
        this.autoCompleteTravelGroups = []

        notifyService.fetch_error('travel_group', e)
      } finally {
        this.loadingAutoCompleteTravelGroups = false
      }
    },
    loadAutoCompleteTravelGroups: debounce(function() {
      this.loadAutoCompleteTravelGroupsAsync()
    }, 500),
    onSelectedTravelGroup(item) {
      this.selectedAutoCompleteTravelGroup = item
      this.form.name = item.name
      this.form.group_code = item.code
      this.form.area_code = item.area_code
      this.form.product_code = item.product_code.trim()
      this.form.catalog_no = item.catalog_list
      this.form.group_departure_date = item.departure_date
      this.form.picture = ''
    },
    async onSend() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return
      }

      this.$emit('send', this.form)
    },
    async onSelectPictureImage() {
      try {
        this.form.picture = await OpenProjectFinder()
      } catch (e) {
        notifyService.error(e)
      }
    },
    onClickImage(url) {
      this.isImageModalActive = true
      this.selectedImageUrl = url
    }
  }
}
</script>
